<template>
    <Intersect @enter.once="onEnter" :threshold="[0.1]">
        <div class="partial-container" style="">
            <template v-for="{ key, value } in orderedPartials">
                <div
                    :key="key"
                    :style="wrapperStyle(key)"
                    :class="{ 'not-valid-component': !isValidComponent(key) }"
                >
                    <component
                        :is="componentNameHandler(key)"
                        :content="value"
                        :key="key"
                        v-if="isValidComponent(key)"
                        v-bind="componentProps(key)"
                    />
                </div>
            </template>
        </div>
    </Intersect>
</template>

<script>
import {
    validComponents,
    rankingComponents,
} from '@organisms/SEOArticlePartials/validComponents';

import IntroParagraph from '@organisms/SEOArticlePartials/partials/IntroParagraph';
import ParagraphText from '@organisms/SEOArticlePartials/partials/ParagraphText';
import SubHeader from '@organisms/SEOArticlePartials/partials/SubHeader';
import SubSubHeader from '@organisms/SEOArticlePartials/partials/SubSubHeader';
import SubSubSubHeader from '@organisms/SEOArticlePartials/partials/SubSubSubHeader';
import ArticleImage from '@organisms/SEOArticlePartials/partials/ArticleImage';
import HighlightedTopProvider from '@organisms/SEOArticlePartials/partials/HighlightedTopProvider';
import DetailedProviderRow from '@organisms/SEOArticlePartials/partials/DetailedProviderRow';

import TopProviderDiscount from '@organisms/SEOArticlePartials/partials/TopProviderDiscount';
import ProviderDiscount from '@organisms/SEOArticlePartials/partials/ProviderDiscount';

import SimpleTable from '@organisms/SEOArticlePartials/partials/SimpleTable';
import ComparisonTable from '@organisms/SEOArticlePartials/partials/ComparisonTable';
import BoldParagraphText from '@organisms/SEOArticlePartials/partials/BoldParagraphText';

import Intersect from 'vue-intersect';
export default {
    name: 'PartialHandler',
    components: {
        Intersect,
        IntroParagraph,
        SubHeader,
        SubSubHeader,
        SubSubSubHeader,
        ParagraphText,
        ArticleImage,
        HighlightedTopProvider,
        TopProviderDiscount,
        SimpleTable,
        ComparisonTable,
        BoldParagraphText,
        ProviderDiscount,
        DetailedProviderRow,
        ArticleRankings: () =>
            import(
                '@organisms/SEOArticlePartials/partials/ArticleRankings/ArticleRankings'
            ),
        ConclusionText: () =>
            import('@organisms/SEOArticlePartials/partials/ConclusionText'),
        List: () => import('@organisms/SEOArticlePartials/partials/List'),
        Sources: () => import('@organisms/SEOArticlePartials/partials/Sources'),
        Quote: () => import('@organisms/SEOArticlePartials/partials/Quote'),
        ProConsTable: () =>
            import('@organisms/SEOArticlePartials/partials/ProConsTable'),
        ArticleTip: () =>
            import('@organisms/SEOArticlePartials/partials/ArticleTip'),
        NumberedList: () =>
            import('@organisms/SEOArticlePartials/partials/NumberedList'),
        InterlinkingBox: () =>
            import('@molecules/InterLinkingList/InterLinkingList'),
        InterlinkingCarousel: () =>
            import('@molecules/InterLinkingCarousel/InterLinkingCarousel'),
        CompadoArticleRankings: () =>
            import(
                '@organisms/SEOArticlePartials/partials/CompadoArticleRankings.vue'
            ),
        CtaButton: () =>
            import('@organisms/SEOArticlePartials/partials/CtaButton'),
        ContentsTable: () =>
            import('@organisms/SEOArticlePartials/partials/ContentsTable'),
        TopProviderCta: () =>
            import('@organisms/SEOArticlePartials/partials/TopProviderCta'),
        SidebarRanking: () =>
            import(
                '@organisms/SEOArticlePartials/partials/SidebarRanking/SidebarRanking'
            ),
        ProConsHeader: () =>
            import('@organisms/SEOArticlePartials/partials/ProConsHeader'),
        QuickGuide: () =>
            import('@organisms/SEOArticlePartials/partials/QuickGuide'),
        GamesEmbed: () =>
            import('@organisms/SEOArticlePartials/partials/GamesEmbed'),
        VideoEmbed: () =>
            import('@organisms/SEOArticlePartials/partials/VideoEmbed'),
        AdsenseWidget: () =>
            import('@organisms/SEOArticlePartials/partials/AdsenseWidget.vue'),
        ConversionCowboysWidget: () =>
            import(
                '@organisms/SEOArticlePartials/partials/ConversionCowboysWidget.vue'
            ),
    },
    props: {
        partialTypes: {
            type: Object,
        },
        partialLength: {
            type: Number,
        },
        providers: {
            type: Array,
        },
        contentsItems: {
            type: Array,
        },
        index: {
            type: Number,
        },
        altText: {
            type: String,
            default: '',
        },
    },
    computed: {
        orderedPartials() {
            const PRIORITY_ORDER = [
                'bold_paragraph_text',
                'sub_header',
                'sub_sub_header',
                'paragraph_text',
                'pro_cons_header',
                'pro_cons_table',
            ];

            const priorityMap = new Map(
                PRIORITY_ORDER.map((key, index) => [key, index])
            );

            const sortByPriority = (a, b) => {
                const aPriority = priorityMap.get(a.key);
                const bPriority = priorityMap.get(b.key);

                if (aPriority === undefined && bPriority === undefined) {
                    return a.index - b.index;
                }

                if (aPriority === undefined) return 1;
                if (bPriority === undefined) return -1;

                return aPriority - bPriority;
            };

            const withIndex = ([key, value], index) => ({
                key,
                value,
                index,
            });

            const stripIndex = ({ key, value }) => ({ key, value });

            return Object.entries(this.partialTypes)
                .map(withIndex)
                .sort(sortByPriority)
                .map(stripIndex);
        },
    },
    methods: {
        onEnter() {
            const index = this.index + 1;
            if (
                index === this.partialLength - 3 &&
                !(index > this.partialLength)
            ) {
                this.$emit('loadNextPartials');
            }
        },
        wrapperStyle(key) {
            if (key === 'pro_cons_table') {
                return {
                    marginTop: '-10px',
                };
            }
            return {};
        },
        componentProps(name) {
            const components = window.__dyanmic_template_props__ || {};

            // bind ranking list provider data
            if (rankingComponents.includes(name) && this.providers.length > 0) {
                const component = this.componentNameHandler(name);
                return {
                    ...(components[component] || {}),
                    providers: this.providers,
                };
            }

            if (name === 'contents_table') {
                return {
                    contentsItems: this.contentsItems,
                };
            }
            if (name === 'interlinking_carousel') {
                return components['InterLinkingCarousel'];
            }

            if (name === 'interlinking_box') {
                return components['InterLinkingList'];
            }

            return {
                'alt-text': this.altText,
            };
        },
        componentNameHandler(snakeString) {
            if (snakeString === 'text' || snakeString === 'paragraph_text') {
                return 'ParagraphText';
            }

            if (snakeString === 'source') {
                return 'Sources';
            }

            if (snakeString === 'adsense_rsoc_widget') {
                return 'AdsenseWidget';
            }

            return snakeString
                .toLowerCase()
                .replace(/(?:^|_)(\w)/g, (match, group) => group.toUpperCase());
        },
        isValidComponent(componentName) {
            return validComponents.includes(
                this.componentNameHandler(componentName)
            );
        },
    },
};
</script>

<style lang="scss" scoped>
.partial-container {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    grid-row-gap: 10px;

    &:has(.not-valid-component) {
        margin-bottom: 0;
    }
}
</style>
